<template>
  <div>
    <b-card>
      <div
        v-if="!backupData.status"
        class="organism-backup-container card-padding"
      >
        <span class="header__label">Create Backup</span>
      </div>
      <div
        v-if="backupData.status"
        class="organism-backup-container card-padding"
      >
        <span class="header__label">Backup Detail</span>
        <div>
          <b-badge pill :variant="badgeVariant(backupData.status)">{{
            badgeStatus(backupData.status)
          }}</b-badge>
        </div>
      </div>

      <div class="card-padding pt-0 border-gray-bottom">
        <div class="container-fluid p-0">
          <div class="row mt-1">
            <div class="col-12 col-md-5 col-xl-4">
              <atom-input
                v-model="backupData.name"
                title="Backup Name"
                placeholder="Backup Name"
                :isAsterisk="true"
                :isError="isValidateForm && backupData.name === ''"
                errorMessage="Please enter backup name"
                :isDisable="!hasUpdatePermission"
              ></atom-input>
            </div>
            <div class="col-12 col-md-7 col-xl-8 mt-1 mt-md-0">
              <atom-input
                v-model="backupData.description"
                title="Description"
                placeholder="Description"
                :isAsterisk="true"
                :isError="isValidateForm && backupData.description === ''"
                errorMessage="Please enter description"
                :isDisable="!hasUpdatePermission"
              ></atom-input>
            </div>
          </div>
          <div class="row">
            <div class="col-12 col-md-6 mt-1">
              <atom-select
                v-model="backupData.vmServer"
                title="Backup from"
                placeholder="Select VM server"
                :isAsterisk="true"
                :isClearable="false"
                :isError="isValidateForm && backupData.vmServer === ''"
                :isDisable="backupData.status !== '' || !hasUpdatePermission"
                errorMessage="Please select VM server"
                :option="vmList"
                label="ip"
              ></atom-select>
            </div>
            <div class="col-12 col-md-6 mt-1">
              <atom-multi-select
                v-model="backupData.server"
                title="Server"
                placeholder="Select server to Backup"
                :isAsterisk="true"
                :isClearable="false"
                :isDisable="
                  backupData.vmServer === '' || backupData.status !== '' || !hasUpdatePermission
                "
                :isError="
                  isValidateForm &&
                  backupData.vmServer !== '' &&
                  backupData.server === ''
                "
                errorMessage="Please select server"
                :option="vmInventoryList"
              ></atom-multi-select>
            </div>
          </div>
        </div>
      </div>

      <div class="card-padding border-gray-bottom">
        <div class="container-fluid p-0">
          <div class="row">
            <div class="col-12 col-md-6">
              <atom-select
                v-model="backupData.repository"
                title="Backup to"
                placeholder="Select repository"
                :isAsterisk="true"
                :isClearable="false"
                :isError="isValidateForm && backupData.repository === ''"
                errorMessage="Please select repository"
                :option="repositoryList"
                :isDisable="!hasUpdatePermission"
              ></atom-select>
            </div>
            <div class="col-12 col-md-6 d-flex align-items-center mt-1 mt-md-0">
              <atom-input
                v-model="backupData.retention"
                title="Retention"
                placeholder="Retention"
                :isAsterisk="true"
                :isError="
                  isValidateForm && !this.isCountingNumber(backupData.retention)
                "
                errorMessage="Please enter retention with number"
                :isDisable="!hasUpdatePermission"
                class="pr-2"
              ></atom-input>
              <div class="mt-2 w-25">Days</div>
            </div>
          </div>
        </div>
      </div>

      <div class="organism-backup-container card-padding">
        <span class="header__label">Schedule</span>
      </div>
      <div class="card-padding pt-0">
        <div class="container-fluid p-0">
          <div class="row">
            <div class="col-12 col-md-3">
              <atom-select
                v-model="backupData.schedule"
                title="Schedule type"
                placeholder="Select schedule type"
                :option="scheduleOption"
                :isDisable="!hasUpdatePermission"
              ></atom-select>
            </div>
          </div>
        </div>
        <div v-if="backupData.schedule === 'Daily'" class="row">
          <div class="col-12 col-md-6 mt-1">
            <atom-multi-select
              v-model="backupData.weekday"
              title="Weekday"
              placeholder="Select weekday"
              :isAsterisk="true"
              :isClearable="false"
              :isError="isValidateForm && backupData.weekday.length === 0"
              errorMessage="Please select weekday"
              :option="weekdayOption"
              :isDisable="!hasUpdatePermission"
            ></atom-multi-select>
          </div>

          <div class="col-12 col-md-4 mt-1">
            <atom-datepicker
              title="Time"
              :isAsterisk="true"
              datepickerId="molecule-date-picker-period--start"
              v-model="backupData.time"
              type="time"
              placeholder="Select time"
              :isDisabled="false || !hasUpdatePermission"
              :isError="isValidateForm && backupData.time === ''"
              errorMessage="Please select time"
            ></atom-datepicker>
          </div>
        </div>

        <div v-if="backupData.schedule === 'Monthly'" class="row mt-1">
          <div class="col-12 col-md-12">
            <atom-multi-select
              v-model="backupData.month"
              title="Month"
              placeholder="Select month"
              :isAsterisk="true"
              :isClearable="false"
              :isError="isValidateForm && !backupData.month.length"
              errorMessage="Please select month"
              :option="monthOption"
              :isDisable="!hasUpdatePermission"
            ></atom-multi-select>
          </div>

          <div class="col-12 col-md-2 mt-1">
            <atom-select
              v-model="backupData.weekOrder"
              title="Week"
              placeholder="Select week"
              :isAsterisk="true"
              :isClearable="false"
              :isError="isValidateForm && !backupData.weekOrder"
              errorMessage="Please select week"
              :option="weekOrderOption"
              :isDisable="!hasUpdatePermission"
            ></atom-select>
          </div>

          <div class="col-12 col-md-6 mt-1">
            <atom-select
              v-model="backupData.monthWeekday"
              title="Weekday"
              placeholder="Select weekday"
              :isAsterisk="true"
              :isClearable="false"
              :isError="isValidateForm && backupData.monthWeekday === ''"
              errorMessage="Please select weekday"
              :option="weekdayOption"
              :isDisable="!hasUpdatePermission"
            ></atom-select>
          </div>

          <div class="col-12 col-md-4 mt-1">
            <atom-datepicker
              title="Time"
              :isAsterisk="true"
              datepickerId="molecule-date-picker-period--start"
              v-model="backupData.monthTime"
              type="time"
              placeholder="Select time"
              :isDisabled="false || !hasUpdatePermission"
              :isError="isValidateForm && backupData.monthTime === ''"
              errorMessage="Please select time"
            ></atom-datepicker>
          </div>
        </div>

        <div v-if="backupData.schedule === 'Periodic'" class="row mt-1">
          <div class="col-12 col-md-6">
            <atom-select
              v-model="backupData.periodic"
              title="Periodic"
              placeholder="Select periodic"
              :isAsterisk="true"
              :isClearable="false"
              :isError="isValidateForm && backupData.periodic === ''"
              errorMessage="Please select periodic"
              :option="periodicOption"
              :isDisable="!hasUpdatePermission"
            ></atom-select>
          </div>

          <div
            v-if="backupData.periodic === 'Minutes'"
            class="col-12 col-md-3 mt-1 mt-md-0"
          >
            <atom-input
              v-model="backupData.amount"
              title="Amount"
              placeholder="Amount"
              :isAsterisk="true"
              :isError="
                isValidateForm && !this.isCountingNumber(backupData.amount)
              "
              errorMessage="Please enter Amount"
              :isDisable="!hasUpdatePermission"
            ></atom-input>
          </div>
          <div
            v-if="backupData.periodic === 'Hours'"
            class="col-12 col-md-3 mt-1 mt-md-0"
          >
            <atom-select
              v-model="backupData.amount"
              title="Amount"
              placeholder="Amount"
              :isClearable="false"
              :isAsterisk="true"
              :isError="
                isValidateForm && !this.isCountingNumber(backupData.amount)
              "
              :option="hourOption"
              errorMessage="Please Select Amount"
              :isDisable="!hasUpdatePermission"
            ></atom-select>
          </div>
        </div>

        <div v-if="backupData.schedule === 'After Job'" class="row mt-1">
          <div class="col-12 col-md-6">
            <atom-select
              v-model="backupData.job"
              title="Job"
              placeholder="Select job"
              :isAsterisk="true"
              :isClearable="false"
              :isError="isValidateForm && backupData.job === ''"
              errorMessage="Please select job"
              :option="backupList"
              label="name"
              :isDisable="!hasUpdatePermission"
            ></atom-select>
          </div>
        </div>

        <div class="row" v-if="this.hasObjectValue(backupData.schedule)">
          <div class="col-12 col-md-6 d-flex align-items-center mt-1">
            <atom-input
              v-model="backupData.retry"
              title="Retry (if failed)"
              placeholder="Retry"
              class="pr-2"
              :isError="
                !(
                  this.isCountingNumber(backupData.retry) ||
                  backupData.retry === 0
                )
              "
              errorMessage="Please enter retry with number"
              :isDisable="!hasUpdatePermission"
            ></atom-input>
            <div class="mt-2 w-25">Times</div>
          </div>
          <div class="col-12 col-md-6 d-flex align-items-center mt-1">
            <atom-input
              v-model="backupData.retryDelay"
              title="Retry Delay"
              placeholder="Retry Delay"
              class="pr-2"
              :isDisable="backupData.retry < 1 || !hasUpdatePermission"
              :isError="!this.isCountingNumber(backupData.retryDelay)"
            ></atom-input>
            <div class="mt-2 w-25">Minutes</div>
          </div>
        </div>
      </div>

      <!-- Restore -->
      <div class="organism-backup-container card-padding mt-1 border-gray-top">
        <span class="header__label">Restore Point</span>
      </div>
      <div class="row">
        <div class="col-12 mt-1 mb-5">
          <b-table
            sticky-header
            responsive
            :items="items"
            :fields="hasUpdatePermission ? fields : fields.filter(field => field.key !== 'restoreIcon')"
            :hover="false"
          >
            <template #cell(restorePoint)="data">
              <div>{{ data.value }}</div>
            </template>
            <template #cell(platform)="data">
              <div>{{ data.value }}</div>
            </template>
            <template #cell(creationTime)="data">
              <div>{{ data.value.date }}</div>
              <div class="sub-header">{{ data.value.time }}</div>
            </template>
            <template #cell(restoreIcon)="data">
              <div :id="'button-restore' + data.item.id">
                <atom-button
                  buttonId="button-restore"
                  iconLeft="UploadCloudIcon"
                  buttonStyle="flat"
                  buttonColor="secondary"
                  @click="clickRestore(data)"
                ></atom-button></div
            ></template>
          </b-table>
          <div class="row card-padding">
            <div class="col-6">Showing {{ items.length }} entries</div>

            <!-- Veeam api has error now but i will resolve paging when api can use corectly  -->
            <!-- <div
              v-if="filterList.totalCount"
              class="col-6 d-flex justify-content-end"
            >
              <b-pagination
                v-model="filterList.currentPage"
                :total-rows="filterList.totalCount"
                :per-page="perPage"
                size="sm"
                class="my-0"
              ></b-pagination>
            </div> -->
          </div>
        </div>
      </div>

      <div v-if="!items.length">
        <div
          class="w-100 d-flex flex-column justify-content-center align-items-center p-4"
        >
          <feather-icon
            icon="CloudOffIcon"
            class="empty-data__icon"
          ></feather-icon>
          <label class="empty-data__text--title">No Restore Point</label>
          <label class="empty-data__text--body">Please Backup</label>
        </div>
      </div>

      <!-- button -->
      <div v-if="!backupData.status && hasUpdatePermission" class="card-padding">
        <div class="d-flex justify-content-end">
          <atom-button
            buttonId="create-backup"
            class="mr-1"
            @click="onClickCreate"
            >Create Backup
          </atom-button>
          <atom-button
            buttonId="cancel-backup"
            buttonStyle="border"
            buttonColor="secondary"
            @click="onClickCancel"
            >Cancel
          </atom-button>
        </div>
      </div>

      <div
        v-if="backupData.status"
        class="card-padding d-flex justify-content-between"
      >
        <div v-if="hasUpdatePermission">
          <atom-button
            v-if="
              backupData.status === 'DISABLED' &&
              backupData.schedule !== '' &&
              isNotChange
            "
            buttonId="create-backup"
            class="mr-1"
            buttonColor="success"
            @click="onClickEnable"
            >Enable
          </atom-button>
          <atom-button
            v-if="
              backupData.status === 'INACTIVE' &&
              backupData.schedule !== '' &&
              isNotChange
            "
            buttonId="cancel-backup"
            buttonStyle="border"
            @click="onClickDisable"
            >Disable
          </atom-button>
        </div>
        <div v-if="hasUpdatePermission" class="d-flex justify-content-end">
          <atom-button
            v-if="backupData.status !== 'RUNNING' && isNotChange"
            buttonId="create-backup"
            class="mr-1"
            @click="onClickStartBackup"
            >Start Backup
          </atom-button>
          <atom-button
            v-else-if="backupData.status !== 'RUNNING'"
            buttonId="create-backup"
            class="mr-1"
            :isDisable="backupData.status === 'RUNNING'"
            @click="onClickSave"
            >Save Change
          </atom-button>
          <atom-button
            v-if="backupData.status === 'RUNNING'"
            buttonId="create-backup"
            class="mr-1"
            @click="onClickStopRunning"
            >Stop Running
          </atom-button>
          <atom-button
            buttonId="cancel-backup"
            buttonStyle="border"
            buttonColor="secondary"
            @click="onClickCancel"
            >Back
          </atom-button>
        </div>
      </div>
    </b-card>

    <!--restore modal-->>
    <section name="modal-restore-vm">
      <molecule-modal-dialog
        modalId="modal-restore-vm"
        :isTextCenter="true"
        icon="AlertCircleIcon"
        textTitle="Do you want to restore?"
        :textSubtitle="`After click restore \n You can not roll back to the current version`"
        buttonTextLeft="Restore"
        buttonColorLeft="primary"
        :isDisableButtonLeft="isDisableRestoreButton"
        :isSlotBody="true"
        @clickButtonLeft="confirmRestore"
        @clickButtonRight="cancelRestore"
        ><template #body
          ><div class="w-70">
            <label
              >Type
              <b>{{ restorePointName }}</b>
              to restore</label
            >
            <atom-input v-model="confrimRestoreMessage"></atom-input>
          </div>
        </template>
      </molecule-modal-dialog>
    </section>

    <footer style="height: 28px"></footer>
  </div>
</template>

<script>
import { BCard, BTable, BPagination, BBadge } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import AtomCheckbox from "@/components/atoms/AtomCheckbox.vue"
import AtomMultiSelect from "@/components/atoms/AtomMultiSelect.vue"
import AtomDatepicker from "@/components/atoms/AtomDatepicker.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import mixinsValidator from "@/mixins/mixinsValidator"
import { PermissionUser } from "@/constants/permission-user"
import mixinsUser from "@/mixins/mixinsUser"

export default {
  name: "OrganismCreateEditBackup",
  components: {
    BCard,
    BTable,
    BPagination,
    BBadge,
    AtomButton,
    AtomInput,
    AtomSelect,
    AtomCheckbox,
    AtomMultiSelect,
    AtomDatepicker,
    MoleculeModalDialog,
  },
  mixins: [mixinsValidator, mixinsUser],
  props: {
    // filterList: {
    //   required: true,
    //   type: Object,
    // },
    value: {
      type: Object,
      required: true,
    },
    backupDataOriginal: {
      type: Object,
      required: true,
    },
    vmList: {
      type: Array,
      required: false,
      default: () => [],
    },
    repositoryList: {
      type: Array,
      required: false,
      default: () => [],
    },
    backupList: {
      type: Array,
      required: false,
      default: () => [],
    },
    vmInventoryList: {
      type: Array,
      required: false,
      default: () => [],
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    fields: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  emits: [
    "input",
    "changeVm",
    "onClickCreate",
    "enableBackup",
    "startBackup",
    "editBackup",
    "disableBackup",
    "stopBackup",
    "confirmRestore",
  ],
  data() {
    return {
      isValidateForm: false,
      // perPage: 10,
      vmServerOption: ["VM Server 1", "VM Server 2"],
      serverOption: ["Server 1", "Server 2"],
      repositoryOption: ["Repository 1", "Repository 2"],
      scheduleOption: ["Daily", /*"Monthly",*/ "Periodic", "After Job"],
      weekdayOption: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      monthOption: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      weekOrderOption: ["First", "Second", "Third", "Fourth"],
      periodicOption: ["Minutes", "Hours"],
      jobOption: ["job 1", "job 2", "job 3"],
      hourOption: [1, 2, 3, 4, 6, 8, 12, 24],
      restorePointName: "",
      restoreObjectId: "",
      confrimRestoreMessage: "",
    }
  },
  computed: {
    hasUpdatePermission() {
      return this.hasPermission(PermissionUser.BMM.BACKUP_WRITE)
    },

    backupData: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      },
    },
    isFormSuccess() {
      return (
        this.backupData.name !== "" &&
        this.backupData.description !== "" &&
        this.backupData.vmServer !== "" &&
        this.backupData.server !== "" &&
        this.backupData.repository !== "" &&
        this.isCountingNumber(this.backupData.retention) &&
        (!this.hasObjectValue(this.backupData.schedule) ||
          (this.backupData.schedule === "Daily" &&
            this.backupData.weekday.length !== 0 &&
            this.backupData.time !== "") ||
          (this.backupData.schedule === "Monthly" &&
            this.backupData.month.length !== 0 &&
            this.backupData.weekOrder !== "" &&
            this.backupData.monthWeekday !== "" &&
            this.backupData.monthTime !== "") ||
          (this.backupData.schedule === "Periodic" &&
            this.backupData.periodic !== "" &&
            this.backupData.amount !== "") ||
          (this.backupData.schedule === "After Job" &&
            this.backupData.job !== ""))
      )
    },

    isNotChange() {
      return (
        JSON.stringify(this.backupData) ===
        JSON.stringify(this.backupDataOriginal)
      )
    },

    isDisableRestoreButton() {
      return this.confrimRestoreMessage != this.restorePointName
    },
  },
  watch: {
    "backupData.vmServer"() {
      this.backupData.server = ""
      if (!this.isEmptyObject(this.backupData.vmServer)) {
        this.$emit("changeVm", this.backupData.vmServer.ip)
      }
    },
    "backupData.periodic"() {
      this.backupData.amount = ""
    },
    // "filterList.currentPage": {
    //   deep: true,
    //   handler(newValue, oldValue) {
    //     if (newValue !== oldValue) {
    //       this.$emit("onUpdateCurrentPage", this.filterList)
    //     }
    //   },
    // },
  },
  methods: {
    onClickCreate() {
      this.isValidateForm = true
      if (this.isFormSuccess) {
        this.isValidateForm = false
        this.$emit("onClickCreate")
      }
    },

    onClickCancel() {
      this.$router.push("/backup-management/backup")
    },

    badgeVariant(status) {
      if (status === "INACTIVE") return "light-warning"
      else if (status === "RUNNING") return "light-success"
      else return "light-secondary"
    },

    badgeStatus(status) {
      if (status === "INACTIVE") return "Inactive"
      else if (status === "RUNNING") return "Running"
      else return "Disabled"
    },

    onClickEnable() {
      this.$emit("enableBackup")
    },

    onClickDisable() {
      this.$emit("disableBackup")
    },

    onClickStartBackup() {
      this.$emit("startBackup")
    },

    onClickSave() {
      this.$emit("editBackup")
    },

    onClickStopRunning() {
      this.$emit("stopBackup")
    },

    clearSchedule() {
      this.backupData.weekday = []
      this.backupData.time = ""
      this.backupData.month = []
      this.backupData.weekOrder = "First"
      this.backupData.monthWeekday = ""
      this.backupData.monthTime = ""
      this.backupData.periodic = ""
      this.backupData.amount = 1
      this.backupData.job = ""
    },
    clickRestore(row) {
      this.openModal("modal-restore-vm")
      this.restorePointName = row.item.name
      this.restoreObjectId = row.item.id
    },
    confirmRestore() {
      this.confrimRestoreMessage = ""
      this.$emit("confirmRestore", this.restoreObjectId)
    },
    cancelRestore() {
      this.confrimRestoreMessage = ""
      this.restorePointName = ""
    },
    openModal(id) {
      if (this.modalId) this.closeModal()
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },
    // close modal
    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
      this.isValidateTrigger = false
    },
  },
}
</script>

<style lang="scss" scoped>
.organism-backup-container {
  display: flex;
  justify-content: space-between;
}

.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.card-padding {
  padding: 1.5rem;
}

.card-body {
  padding: 0;
}

.border-gray-bottom {
  border-bottom: 1px solid #ebe9f1;
}

.border-gray-top {
  border-top: 1px solid #ebe9f1;
}
.sub-header {
  font-weight: 400;
  font-size: 12px;
  color: #b9b9c3;
}

.empty-data__icon {
  width: 100px;
  height: 100px;
}

.empty-data__text--title {
  color: #5e5873;
  font-weight: 500;
  font-size: 18px;
  margin-top: 20px;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
  .organism-backup-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}

// X-Large devices (large desktops, 1200px and up)
@media screen and (min-width: 1200px) {
}
</style>
